type ArtworkBounds = {
	x: number
	y: number
	width: number
	height: number
}

const normalizedArtwork = new Map<string, Promise<string>>()
const canvasEdge = 512

function artworkBounds(pixels: ImageData): ArtworkBounds {
	let left = pixels.width
	let top = pixels.height
	let right = -1
	let bottom = -1
	for (let y = 0; y < pixels.height; y++) {
		for (let x = 0; x < pixels.width; x++) {
			if (pixels.data[(y * pixels.width + x) * 4 + 3] < 32) continue
			left = Math.min(left, x)
			top = Math.min(top, y)
			right = Math.max(right, x)
			bottom = Math.max(bottom, y)
		}
	}
	if (right < left || bottom < top)
		throw new Error('Embroidery artwork has no visible pixels')
	return {
		x: left,
		y: top,
		width: right - left + 1,
		height: bottom - top + 1,
	}
}

function fittedArtwork(
	bounds: ArtworkBounds,
	targetHeight = 300,
	matchHeight = false
): ArtworkBounds {
	const heightScale = targetHeight / bounds.height
	const width = Math.min(bounds.width * heightScale, 480)
	const height = matchHeight
		? targetHeight
		: bounds.height * Math.min(heightScale, 480 / bounds.width)
	return {
		x: (canvasEdge - width) / 2,
		y: (canvasEdge - height) / 2,
		width,
		height,
	}
}

async function normalize(
	url: string,
	targetHeight: number,
	matchHeight: boolean
) {
	const image = new Image()
	image.crossOrigin = 'anonymous'
	image.src = url
	await image.decode()
	if (!image.naturalWidth || !image.naturalHeight)
		throw new Error('Embroidery artwork has no dimensions')
	const source = document.createElement('canvas')
	const sourceScale =
		canvasEdge / Math.max(image.naturalWidth, image.naturalHeight)
	source.width = Math.max(1, Math.round(image.naturalWidth * sourceScale))
	source.height = Math.max(1, Math.round(image.naturalHeight * sourceScale))
	const context = source.getContext('2d', { willReadFrequently: true })
	if (!context) throw new Error('Embroidery canvas unavailable')
	context.drawImage(image, 0, 0, source.width, source.height)
	const bounds = artworkBounds(
		context.getImageData(0, 0, source.width, source.height)
	)
	const fit = fittedArtwork(bounds, targetHeight, matchHeight)
	const output = document.createElement('canvas')
	output.width = output.height = canvasEdge
	const target = output.getContext('2d')
	if (!target) throw new Error('Embroidery canvas unavailable')
	target.drawImage(
		source,
		bounds.x,
		bounds.y,
		bounds.width,
		bounds.height,
		fit.x,
		fit.y,
		fit.width,
		fit.height
	)
	return output.toDataURL('image/png')
}

export function normalizeEmbroideryArtwork(
	url: string,
	targetHeight = 300,
	matchHeight = false
): Promise<string> {
	const cacheKey = JSON.stringify([url, targetHeight, matchHeight])
	const cached = normalizedArtwork.get(cacheKey)
	if (cached) return cached
	const pending = normalize(url, targetHeight, matchHeight)
	normalizedArtwork.set(cacheKey, pending)
	if (normalizedArtwork.size > 128) {
		const oldest = normalizedArtwork.keys().next().value
		if (oldest !== undefined) normalizedArtwork.delete(oldest)
	}
	void pending.catch(() => {
		if (normalizedArtwork.get(cacheKey) === pending)
			normalizedArtwork.delete(cacheKey)
	})
	return pending
}
